import './ExpandableRow.scss';
import { fetchNodeInstanceById } from '../../redux/slices/node_instancesSlice';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { FiEye } from 'react-icons/fi';
import { useEffect, type ReactNode } from 'react';
import Chevron from 'components/Chevron/Chevron';

interface ExpandableRowProps {
  row: any;
  theme: string;
  showFlowInstance: (row: any) => void;
  isExpanded?: boolean;
  onToggle?: () => void;
  // Per-row indicators the page owns, rendered after the eye. They live inside
  // this flex row rather than as a sibling of it so they inherit the eye's
  // chevron-independent alignment (see the padding swap in the stylesheet).
  trailing?: ReactNode;
}

const ExpandableRow = ({ row, theme, showFlowInstance, isExpanded, onToggle, trailing }: ExpandableRowProps) => {
  const dispatch = useAppDispatch();
  const { original = {}, getCanExpand, depth } = row;
  const paddingLeft = `${depth}rem`;
  const lastUpdated = useAppSelector((state) => state.nodeInstances);

  const nodeId = typeof original.node_instance_id === 'string' ? parseInt(original.node_instance_id, 10) : original.node_instance_id;

  const hasChildren = (original.child_nodes?.length ?? 0) > 0;

  useEffect(() => {
    if (!hasChildren && row.getIsExpanded()) {
      row.toggleExpanded(false);
    }
  }, [hasChildren, row]);

  useEffect(() => {
    if (isExpanded === undefined || !hasChildren) return;

    const tableExpanded = row.getIsExpanded();
    if (isExpanded !== tableExpanded) {
      row.toggleExpanded(isExpanded);
    }
  }, [isExpanded, hasChildren, row]);

  const handleExpand = async () => {
    const reduxNode = lastUpdated.selectedNodeInstance.find((n) => {
      const nId = typeof n.node_instance_id === 'string' ? parseInt(n.node_instance_id, 10) : n.node_instance_id;
      return nId === nodeId;
    });

    const needsFetch =
      !reduxNode || reduxNode?.child_nodes?.length !== original?.child_nodes?.length || reduxNode?.child_edges?.length !== original?.child_edges?.length;

    if (needsFetch) {
      await dispatch(fetchNodeInstanceById(nodeId));
    }

    if (onToggle) {
      onToggle();
    } else {
      row.toggleExpanded();
    }
  };

  const expanded = isExpanded !== undefined ? isExpanded : row.getIsExpanded();

  return (
    <div className="d-flex w-100" style={{ paddingLeft }}>
      {getCanExpand() && <Chevron expanded={expanded && hasChildren} onToggle={handleExpand} className={`expandable-indicator ${theme}`} />}
      <button className={`expandable-header ${getCanExpand() ? 'flow-node' : ''}`} onClick={() => showFlowInstance(row)}>
        <FiEye />
      </button>
      {trailing}
    </div>
  );
};

export default ExpandableRow;
